<template>
  <div class="pa-boosters">
    <div v-if="boostersLoading && !boostersState" class="pa-boosters__loading mainColor--text">
      Loading booster packs…
    </div>

    <div v-else-if="boostersError && !boostersState" class="pa-trackcard">
      <i class="material-icons" aria-hidden="true">warning</i>
      <p><b>We could not load booster packs.</b> {{ boostersError }}</p>
      <button type="button" class="pa-btn pa-btn--orange-out" @click="loadBoosters()">Retry</button>
    </div>

    <template v-else>
      <!-- the campaign manager's recommendation (design: boosters_needed card), dismissible -->
      <div v-if="boosterRecommendation" class="pa-trackcard pa-boosters__recommend">
        <i class="material-icons" aria-hidden="true">recommend</i>
        <p>
          <b>Recommended: {{ boosterRecommendation.boosterName }}.</b>
          Your SEO Manager recommends a {{ boosterRecommendation.packLabel }} for “{{ siteName }}”.
          <template v-if="boosterRecommendation.note"> {{ boosterRecommendation.note }}</template>
        </p>
        <div class="pa-boosters__recommend-actions">
          <button type="button" class="pa-btn pa-btn--orange-out pa-btn--pill" @click="selectRecommendedPack">Select this pack</button>
          <a
            v-if="campaignManagerEmail"
            class="pa-btn pa-btn--out pa-btn--pill"
            :href="`mailto:${campaignManagerEmail}`"
          >Message your SEO Manager</a>
          <button
            type="button"
            class="pa-act"
            title="Dismiss"
            :disabled="boostersBusy"
            @click="dismissRecommendation"
          ><i class="material-icons" aria-hidden="true">close</i></button>
        </div>
      </div>

      <!-- design BoostersTab: one muted line with the open orders, above the heading -->
      <p v-if="orderCount > 0" class="pa-boosters__orders">
        You have {{ orderCount }} booster {{ orderCount === 1 ? 'order' : 'orders' }}. Track deliveries under
        <router-link :to="deliverablesRoute">Deliverables</router-link>; invoices are in
        <router-link :to="orderHistoryRoute">Order history</router-link>.
      </p>

      <h3 class="pa-boosters__title">Booster packs</h3>
      <p class="pa-boosters__intro">
        Add booster packs at any time to accelerate this project's results. These are the trust and authority
        signals that separate businesses that rank from businesses that dominate. Purchases are one-time, per
        project, and billed separately from your monthly package.
      </p>

      <div v-if="!canBuy" class="pa-note pa-note--gray">
        <i class="material-icons" aria-hidden="true">lock</i>
        <span>{{ canBuyMessage }}</span>
      </div>

      <div v-if="isAdmin" class="pa-boosters__admin">
        <div class="pa-boosters__admin-head">
          <span class="pa-boosters__admin-label">Admin</span>
          <b>Recommend a booster</b>
        </div>
        <div>The client sees a dismissible card on their Overview and Boosters tab, and gets one email in your voice.</div>
        <div v-if="currentRecommendation" class="pa-boosters__admin-current">
          Current: {{ currentRecommendation.label }} · sent {{ popAuthorityDateLabel(currentRecommendation.recommendedAt) }}
          <template v-if="currentRecommendation.dismissedAt">
            · dismissed {{ popAuthorityDateLabel(currentRecommendation.dismissedAt) }}
          </template>
          <template v-else-if="currentRecommendation.fulfilledByOrderId"> · bought</template>
        </div>
        <button
          type="button"
          class="pa-btn pa-btn--out pa-btn--admin"
          :disabled="boostersBusy"
          @click="recommendOpen = true"
        ><i class="material-icons" aria-hidden="true">campaign</i>Recommend (admin)</button>
      </div>

      <div class="pa-boostgrid">
        <article
          v-for="booster in boosterCatalogue"
          :key="booster.key"
          :ref="'boost-' + booster.key"
          class="pa-boost"
        >
          <div class="pa-boost__ic"><i class="material-icons" aria-hidden="true">{{ booster.icon || 'bolt' }}</i></div>
          <h4>{{ booster.name }}</h4>
          <p class="pa-boost__desc">{{ booster.description }}</p>
          <div class="pa-boost__from">{{ booster.from_label }}</div>
          <div class="pa-boost__packs" role="radiogroup" :aria-label="`${booster.name} packs`">
            <button
              v-for="pack in booster.packs"
              :key="pack.key"
              type="button"
              class="pa-packrow"
              :class="{ 'pa-packrow--sel': isSelected(booster, pack) }"
              role="radio"
              :aria-checked="isSelected(booster, pack) ? 'true' : 'false'"
              @click="selectPack(booster.key, pack.key)"
            >
              <span class="pa-packrow__label">
                {{ pack.label }}
                <span v-if="pack.save" class="pa-packrow__save">Save ${{ money(pack.save) }}</span>
                <span v-if="pack.note" class="pa-packrow__note">{{ pack.note }}</span>
              </span>
              <b class="pa-packrow__price">${{ money(pack.price) }}</b>
            </button>
          </div>
          <div class="pa-boost__actions">
            <button
              type="button"
              class="pa-btn pa-btn--orange"
              :disabled="boostersBusy || !canBuy || !selectedPackFor(booster)"
              @click="openPurchase(booster, 'checkout')"
            >
              Buy {{ selectedLabel(booster) }} for ${{ money(selectedPrice(booster)) }}
              <i class="material-icons" aria-hidden="true">shopping_cart</i>
            </button>
            <button
              v-if="isAdmin"
              type="button"
              class="pa-btn pa-btn--out pa-btn--admin"
              :disabled="boostersBusy || !selectedPackFor(booster)"
              @click="openPurchase(booster, 'invoice')"
            >
              <i class="material-icons" aria-hidden="true">receipt_long</i>
              Send invoice (admin)
            </button>
          </div>
        </article>
      </div>
    </template>

    <booster-confirm-modal
      v-if="confirm"
      v-bind="confirmProps"
      :busy="boostersBusy"
      @close="confirm = null"
      @confirm="submitPurchase"
    />

    <booster-recommend-modal
      v-if="recommendOpen"
      :catalogue="boosterCatalogue"
      :current="currentRecommendation"
      :site-name="siteName"
      :busy="boostersBusy"
      @close="recommendOpen = false"
      @confirm="recommend"
    />
  </div>
</template>

<script>
import BoosterConfirmModal from '@/components/SitesSingleView/PopAuthority/BoosterConfirmModal.vue'
import BoosterRecommendModal from '@/components/SitesSingleView/PopAuthority/BoosterRecommendModal.vue'
import boostersMixin from '@/components/SitesSingleView/PopAuthority/boostersMixin'
import {
  popAuthorityBoosterCount,
  popAuthorityBoosterPack,
  popAuthorityDateLabel,
  showPopAuthorityToast,
} from '@/utils/popAuthority'

const CAN_BUY_MESSAGES = {
  closed: 'This project is closed. Ask your SEO Manager to reopen it before adding boosters.',
  setup_pending: 'Boosters become available once your setup fee is paid.',
}
const CHECKOUT_POLL_ATTEMPTS = 3
const CHECKOUT_POLL_DELAY_MS = 2500

/**
 * POP Authority Boosters tab (design pa-tabs.jsx BoostersTab + pa-app.jsx purchase / invoice modals):
 * the catalogue with one selected pack per booster, "Buy" (Stripe Checkout through the shared
 * createStripePayment flow, like the setup fee), admin "Send invoice" and "Recommend", and the
 * campaign manager's recommendation card. Delivery lives under Deliverables → Boosters, billing
 * under Order history.
 */
export default {
  name: 'BoostersPopAuthority',
  components: { BoosterConfirmModal, BoosterRecommendModal },
  mixins: [boostersMixin],
  data () {
    return {
      /** booster key -> selected pack key */
      selectedPack: {},
      /** { kind: 'checkout' | 'invoice', booster, pack } */
      confirm: null,
      recommendOpen: false,
      redirecting: false,
      destroyed: false,
    }
  },
  computed: {
    siteId () {
      return this.boosterSiteId
    },
    site () {
      return this.$store.getters.siteById(this.siteId) || null
    },
    siteName () {
      return (this.site && this.site.name) || 'your site'
    },
    isMissionControlSession () {
      return Boolean(this.$store.state.selectedSubAccountRelation?.isMissionControlSubAccount)
    },
    isAdmin () {
      return this.boostersIsAdmin && this.isMissionControlSession && Boolean(this.$store.state.isWhiteGloveAdminMode)
    },
    /** The billing email the invoice goes to: the owner of the account being worked on (mission control acts through a relation), preferring its "Send invoices to" address. */
    ownerEmail () {
      const r = this.$store.state.selectedSubAccountRelation
      const user = this.$store.state.user
      return (r && (r.agencyInvoicingEmail || r.agencyUserEmail)) || (user && (user.invoicingEmail || user.email)) || 'the site owner'
    },
    orderCount () {
      return popAuthorityBoosterCount(this.boosterOrders)
    },
    canBuy () {
      return !this.boostersState || this.boostersState.canBuy !== false
    },
    canBuyMessage () {
      const reason = this.boostersState && this.boostersState.canBuyReason
      return CAN_BUY_MESSAGES[reason] || 'Boosters are not available for this project right now.'
    },
    campaignManagerEmail () {
      return (this.boostersState && this.boostersState.campaignManagerEmail) || ''
    },
    /** The stored recommendation, open or not (the admin block shows its history). */
    currentRecommendation () {
      return (this.boostersState && this.boostersState.recommendation) || null
    },
    deliverablesRoute () {
      return { name: 'AllContentWhiteGlove', params: { siteId: String(this.siteId) }, query: { tab: 'boosters' } }
    },
    orderHistoryRoute () {
      return { name: 'OrderHistoryWhiteGlove', params: { siteId: String(this.siteId) } }
    },
    confirmProps () {
      const c = this.confirm
      if (!c) return {}
      const price = this.money(c.pack.price)
      // design pa-app.jsx invoiceBooster / purchaseBooster: icon, body, one price row, orange confirm
      if (c.kind === 'invoice') {
        return {
          icon: 'receipt_long',
          title: `Send invoice: ${c.booster.name} (admin)`,
          body: `Emails an invoice for “${c.pack.label}” to ${this.ownerEmail}. `
            + 'The booster is added to Order history as Invoice sent and moves to Ordered when paid.',
          lines: [[c.pack.label, c.pack.price]],
          confirmLabel: 'Send invoice',
        }
      }
      return {
        icon: 'bolt',
        title: `Purchase ${c.booster.name}`,
        body: `“${c.pack.label}” for ${this.siteName}. This is a one-time purchase billed to your card on file, `
          + 'separate from your monthly package.',
        lines: [[c.pack.label, c.pack.price]],
        confirmLabel: `Confirm purchase: $${price}`,
      }
    },
  },
  watch: {
    '$store.getters.isPopAuthorityUser': {
      // Loads once the account type is known; White Glove sessions never use this tab
      handler (v, old) {
        if (v === false && this.$store.state.user && this.$store.state.user.id) this.redirectToOverview()
        if (v && !old) this.loadBoosters().then(() => this.handleQuery())
      },
      immediate: true,
    },
    '$route.params.siteId' () {
      this.selectedPack = {}
      this.loadBoosters().then(() => this.handleQuery())
    },
    '$store.state.triggerRockyRefresh' () {
      this.loadBoosters({ silent: true })
    },
  },
  beforeDestroy () {
    this.destroyed = true
  },
  methods: {
    popAuthorityDateLabel,
    money (v) {
      const n = Number(v) || 0
      return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
    },
    redirectToOverview () {
      const siteId = this.$route.params.siteId
      if (siteId == null || siteId === '') {
        this.$router.replace('/sites')
        return
      }
      this.$router.replace({ name: 'OverviewWhiteGlove', params: { siteId: String(siteId) } })
    },
    /** Mixin hook: keep one selected pack per booster (first pack, or the recommended one). */
    applyBoosters (data) {
      this.boostersState = data
      this.ensureSelections()
    },
    ensureSelections () {
      const rec = this.boosterRecommendation
      const next = { ...this.selectedPack }
      for (const booster of this.boosterCatalogue) {
        const packs = booster.packs || []
        const valid = packs.some((p) => p.key === next[booster.key])
        if (valid) continue
        const recommended = rec && rec.boosterKey === booster.key && packs.find((p) => p.key === rec.packKey)
        next[booster.key] = (recommended || packs[0] || {}).key || ''
      }
      this.selectedPack = next
    },
    selectPack (boosterKey, packKey) {
      this.selectedPack = { ...this.selectedPack, [boosterKey]: packKey }
    },
    isSelected (booster, pack) {
      return this.selectedPack[booster.key] === pack.key
    },
    selectedPackFor (booster) {
      return (booster.packs || []).find((p) => p.key === this.selectedPack[booster.key]) || null
    },
    selectedLabel (booster) {
      const pack = this.selectedPackFor(booster)
      return pack ? pack.label : ''
    },
    selectedPrice (booster) {
      const pack = this.selectedPackFor(booster)
      return pack ? pack.price : 0
    },
    scrollToBooster (boosterKey) {
      this.$nextTick(() => {
        const refs = this.$refs['boost-' + boosterKey]
        const el = Array.isArray(refs) ? refs[0] : refs
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    selectRecommendedPack () {
      const rec = this.boosterRecommendation
      if (!rec) return
      if (popAuthorityBoosterPack(rec.boosterKey, rec.packKey, this.boosterCatalogue)) {
        this.selectPack(rec.boosterKey, rec.packKey)
      }
      this.scrollToBooster(rec.boosterKey)
    },
    dismissRecommendation () {
      return this.runBoosterAction(
        'dismissPopAuthorityBoosterRecommendation',
        { siteId: this.siteId },
        'Recommendation dismissed.'
      )
    },
    recommend ({ boosterKey, packKey, note }) {
      return this.runBoosterAction(
        'recommendPopAuthorityBooster',
        { siteId: this.siteId, body: { boosterKey, packKey, note: note || null } },
        'Recommendation sent. The client sees it on their Overview and Boosters tab.'
      ).then((data) => {
        if (data) this.recommendOpen = false
      })
    },
    openPurchase (booster, kind) {
      const pack = this.selectedPackFor(booster)
      if (!pack) return
      this.confirm = { kind, booster, pack }
    },
    /**
     * Two steps, like the setup fee: prepare the booster order, then hand its `payment` body to the
     * shared createStripePayment action (Checkout redirect, or the emailed invoice for admins).
     */
    submitPurchase () {
      const c = this.confirm
      if (!c || this.boostersBusy) return Promise.resolve()
      const mode = c.kind === 'invoice' ? 'invoice' : 'checkout'
      this.boostersBusy = true
      this.$store.commit('showLoading')
      return this.$store
        .dispatch('createPopAuthorityBoosterOrder', {
          siteId: this.siteId,
          body: { boosterKey: c.booster.key, packKey: c.pack.key, mode },
        })
        .then((response) => {
          const data = (response && response.data) || {}
          if (data.status !== 'SUCCESS' || !data.payment) {
            this.$notify({ group: 'info', type: 'error', text: data.msg || 'Could not prepare the order.' })
            return null
          }
          this.applyBoosters(data)
          return this.$store.dispatch('createStripePayment', data.payment)
        })
        .then((response) => {
          if (!response) return
          const data = (response && response.data) || {}
          if (data.url) {
            this.redirecting = true
            window.location.href = data.url
            return
          }
          if (mode === 'invoice' && data.status === 'SUCCESS') {
            this.confirm = null
            showPopAuthorityToast(
              this,
              `Invoice sent to ${this.ownerEmail}. The booster shows as Invoice sent in Order history.`
            )
            return this.loadBoosters({ silent: true }).then(() => {
              if (typeof this.refreshWhiteGloveOrders === 'function') this.refreshWhiteGloveOrders()
            })
          }
          this.$notify({
            group: 'info',
            type: 'error',
            text: data.msg || data.message || 'Could not start the payment.',
          })
        })
        .catch((e) => {
          const data = (e && e.response && e.response.data) || {}
          this.$notify({ group: 'info', type: 'error', text: data.msg || data.message || 'Could not start the payment.' })
        })
        .finally(() => {
          if (this.redirecting) return // stay busy until Stripe loads
          this.boostersBusy = false
          this.$store.commit('hideLoading')
        })
    },
    /** Back from Stripe (?checkout=success|canceled) or from the delivered email (?reorder=booster:pack). */
    handleQuery () {
      const query = this.$route.query || {}
      if (query.checkout === 'success') {
        showPopAuthorityToast(
          this,
          'Thanks! Your booster is ordered. Track its delivery under Deliverables; the receipt is in Order history.'
        )
        this.stripQuery(['checkout'])
        this.pollCheckout()
      } else if (query.checkout === 'canceled') {
        showPopAuthorityToast(this, 'Checkout canceled. Nothing was charged.')
        this.stripQuery(['checkout'])
      }
      if (query.reorder) {
        const [boosterKey, packKey] = String(query.reorder).split(':')
        if (popAuthorityBoosterPack(boosterKey, packKey, this.boosterCatalogue)) {
          this.selectPack(boosterKey, packKey)
          this.scrollToBooster(boosterKey)
        }
        this.stripQuery(['reorder'])
      }
    },
    /** The webhook lands a moment after the redirect: refresh a few times so the new order shows up. */
    pollCheckout (attempt = 0) {
      if (this.destroyed || attempt >= CHECKOUT_POLL_ATTEMPTS) return
      setTimeout(() => {
        if (this.destroyed) return
        this.loadBoosters({ silent: true }).then(() => {
          if (typeof this.refreshWhiteGloveOrders === 'function') this.refreshWhiteGloveOrders()
          this.pollCheckout(attempt + 1)
        })
      }, CHECKOUT_POLL_DELAY_MS)
    },
    stripQuery (keys) {
      const query = { ...this.$route.query }
      let changed = false
      for (const key of keys) {
        if (key in query) {
          delete query[key]
          changed = true
        }
      }
      if (!changed) return
      this.$router.replace({ name: this.$route.name, params: this.$route.params, query }).catch(() => {})
    },
  },
}
</script>

<style lang="scss">
/* The global `* { font-size: .85rem; line-height: 18px }` rule (assets/main.scss) would override the
   .9rem / 1.45 base of the design (pa.css .pa-app) on every descendant; inherit instead (icon fonts
   keep their own size). Unscoped on purpose: the rule must reach the children's DOM. */
.pa-boosters :where(*:not(.material-icons):not(.material-icons-outlined):not(.material-icons-round):not(.mdi):not(.fa)) {
  font-size: inherit;
  line-height: inherit;
}
</style>

<style lang="scss" scoped>
/* Design pa-tabs.jsx BoostersTab + pa.css "boosters" (cards and pack rows in setup.scss). */
@import './setup/setup.scss';

.pa-boosters {
  padding: 4px 0 40px;
  color: $pa-teal;
  font-size: 0.9rem;
  line-height: 1.45;

  &__loading {
    font-size: 0.95rem;
    margin: 16px 0;
  }

  /* design: "You have N booster orders..." .85rem muted, links 700 */
  &__orders {
    font-size: 0.85rem;
    color: $pa-muted;
    margin: 0 0 14px;

    a {
      color: $pa-link;
      font-weight: 700;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  /* pa.css .pa-tlh with the design's 4px bottom margin */
  &__title {
    font-size: 1.05rem;
    font-weight: 800;
    line-height: 1.45;
    color: $pa-teal;
    margin: 0 0 4px;
  }

  /* pa.css .pa-plansub */
  &__intro {
    color: $pa-muted;
    font-size: 0.88rem;
    max-width: 78ch;
    margin: 0 0 18px;
  }

  /* recommendation card: inline bold title, pill CTAs (design .pa-trackcard) */
  &__recommend {
    flex-wrap: wrap;
    max-width: none;

    p b {
      display: inline;
    }
  }

  &__recommend-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-left: auto;
  }

  &__admin {
    border: 1px dashed #c9a3d6;
    background: #fbf3fd;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 18px;
    max-width: 78ch;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 0.85rem;

    .pa-btn {
      align-self: flex-start;
    }
  }

  &__admin-head {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__admin-label {
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 0.7rem;
    font-weight: 800;
    color: #7a3e8f;
    background: #fff;
    border-radius: 999px;
    padding: 2px 8px;
  }

  &__admin-current {
    color: $pa-muted;
  }
}
</style>
